import type { ReactNode } from "react";

export interface FlowChromeProps {
  /** Screen test id on the outer chrome (e.g. screen-home). */
  testId: string;
  /** Extra class for screen-specific layout (home-screen, brief-screen, …). */
  className?: string;
  /** Stage label under brand — brief, publish, etc. */
  stage?: string;
  title?: string;
  /** One short supporting line; omit when the screen has its own hero copy. */
  hook?: string;
  backLabel?: string;
  onBack?: () => void;
  /** Override back button test id (e.g. close-portfolio). */
  backTestId?: string;
  /** Compact economy / status pills in the topbar. */
  economy?: ReactNode;
  /** Primary actions row at the bottom of the chrome. */
  actions?: ReactNode;
  children: ReactNode;
  /** Landmark heading level for the stage title. */
  headingLevel?: 1 | 2;
  /** Extra attrs (e.g. data-contrast). */
  dataAttrs?: Record<string, string | undefined>;
}

/**
 * Shared dark creator chrome for every non-editor flow screen.
 * Full-bleed desk-chrome background; max-width lives on the inner shell only.
 */
export function FlowChrome({
  testId,
  className = "",
  stage,
  title,
  hook,
  backLabel = "Desk",
  onBack,
  backTestId = "flow-back",
  economy,
  actions,
  children,
  headingLevel = 1,
  dataAttrs,
}: FlowChromeProps) {
  const HeadingTag = headingLevel === 2 ? "h2" : "h1";
  const classes = ["desk-app", "desk-chrome", "flow-chrome", "screen", className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} data-testid={testId} {...dataAttrs}>
      <div className="flow-inner">
        <header className="flow-topbar" data-testid="flow-topbar">
          <div className="flow-topbar-leading">
            {onBack ? (
              <button
                type="button"
                className="icon-label-btn flow-back"
                data-testid={backTestId}
                onClick={onBack}
              >
                ← {backLabel}
              </button>
            ) : null}
            <div className="flow-brand-block">
              <p className="brand" data-testid="flow-brand">
                DESK
              </p>
              {stage ? (
                <p className="flow-stage" data-testid="flow-stage">
                  {stage}
                </p>
              ) : null}
            </div>
          </div>
          {economy ? (
            <div className="flow-economy" data-testid="flow-economy">
              {economy}
            </div>
          ) : null}
        </header>

        {(title || hook) && (
          <div className="flow-heading">
            {title ? <HeadingTag className="flow-title">{title}</HeadingTag> : null}
            {hook ? <p className="flow-hook">{hook}</p> : null}
          </div>
        )}

        <main className="flow-main" data-testid="flow-main">
          {children}
        </main>

        {actions ? (
          <footer className="flow-actions cta-row" data-testid="flow-actions">
            {actions}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
